import { useEffect, useState } from "react";

import ReportService from "src/Services/API/report.service";
import Loader from "src/Components/Common/Loader";
import { can, handleError } from "src/Common/function";

/**
 * A two-state switch. Written out rather than a checkbox because the rule is
 * on or off for a whole branch, and a switch reads as a setting where a
 * checkbox reads as a selection.
 */
function Switch({ checked, disabled, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative shrink-0 w-[38px] h-[22px] rounded-full transition-colors duration-150 ${
        checked ? "bg-[#0065FD]" : "bg-[#CBD5E1]"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`absolute top-[2px] left-[2px] w-[18px] h-[18px] rounded-full bg-white shadow transition-transform duration-150 ${
          checked ? "translate-x-[16px]" : "translate-x-0"
        }`}
      />
    </button>
  );
}

/**
 * Lunch recording rules, per branch.
 *
 * Whether a lunch has to be recorded is state law, and one company runs
 * branches in more than one state, so the rule is the branch's — unlike the
 * overtime rule, which is the person's. Two switches per branch: whether the
 * lunch controls exist at all for that branch's people, and whether a worked
 * day with nothing recorded is flagged for payroll. The second cannot be on
 * without the first.
 *
 * Saves on click, the way the overtime drawer does, so there is no
 * half-applied state to reason about.
 */
const LunchRulesDrawer = ({ isOpen, onClose }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingIds, setSavingIds] = useState({});

  const editable = can("time-management:setLunchRules");

  // Double rAF so the panel transitions in on first open — same as OtRuleDrawer.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!isOpen) {
      setVisible(false);
      return undefined;
    }
    let secondId;
    const firstId = requestAnimationFrame(() => {
      secondId = requestAnimationFrame(() => setVisible(true));
    });
    return () => {
      cancelAnimationFrame(firstId);
      if (secondId) cancelAnimationFrame(secondId);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    ReportService.getLunchRules()
      .then((response) => setRules(Array.isArray(response.data) ? response.data : []))
      .catch((error) => handleError(error))
      .finally(() => setLoading(false));
  }, [isOpen]);

  /**
   * Save one switch. Optimistic with rollback, and only the changed key is
   * sent, so a stale row cannot overwrite the other switch. Turning recording
   * off is shown taking the requirement with it, which is what the server
   * does too.
   */
  const patchRule = (rule, patch) => {
    const previous = { allowed: rule.allowed, required: rule.required };
    const next = { ...previous, ...patch };
    if (next.allowed === false) next.required = false;

    setRules((prev) => prev.map((item) => (item.branchId === rule.branchId ? { ...item, ...next } : item)));
    setSavingIds((prev) => ({ ...prev, [rule.branchId]: true }));

    ReportService.setLunchRule(rule.branchId, patch)
      .then((response) => {
        if (response?.data && typeof response.data === "object") {
          setRules((prev) =>
            prev.map((item) => (item.branchId === rule.branchId ? { ...item, ...response.data } : item))
          );
        }
      })
      .catch((error) => {
        setRules((prev) =>
          prev.map((item) => (item.branchId === rule.branchId ? { ...item, ...previous } : item))
        );
        handleError(error);
      })
      .finally(() => {
        setSavingIds((prev) => {
          const copy = { ...prev };
          delete copy[rule.branchId];
          return copy;
        });
      });
  };

  if (!isOpen && !visible) return null;

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black bg-opacity-20 transition-opacity duration-300 ease-in-out ${
          visible ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed top-0 right-0 bottom-0 z-50 flex h-screen w-[520px] max-w-full transform flex-col bg-white shadow-[0_4px_24px_rgba(0,0,0,0.12)] transition-transform duration-300 ease-in-out ${
          visible ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="px-6 pt-5 pb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[18px] leading-6 font-OpenSansSemiBold text-[#1A2332]">Lunch Policies</p>
            <p className="mt-1 text-[13px] leading-[18px] font-OpenSansRegular text-[#868DA6]">
              Set, per branch, whether lunch can be recorded and whether it has to be. Some states
              require it; a branch that does not record lunch shows no lunch controls at all.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-[#868DA6] hover:bg-[#F0F1F5] hover:text-[#1A2332]"
            title="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M12 4L4 12M4 4l8 8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto bg-[#FBFCFF] border-t border-[#E5E8F3] px-4 py-3">
          {loading ? (
            <div className="py-10 flex justify-center">
              <Loader variant="inline" ariaLabel="loading-lunch-policies" />
            </div>
          ) : rules.length === 0 ? (
            <p className="py-10 text-center text-[14px] font-OpenSansRegular text-[#868DA6]">
              No branches found.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {rules.map((rule) => {
                const saving = Boolean(savingIds[rule.branchId]);
                return (
                  <div
                    key={rule.branchId}
                    className="bg-white rounded-[8px] border border-[#E5E8F3] px-4 py-3"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-[14px] leading-5 font-OpenSansSemiBold text-[#1A2332] truncate">
                        {rule.branchName || "Unnamed branch"}
                      </p>
                      {saving ? <Loader variant="inline" ariaLabel="saving" /> : null}
                    </div>

                    <div className="mt-2 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[13px] leading-[18px] font-OpenSansSemiBold text-[#1A2332]">
                          Allow recording lunch
                        </p>
                        <p className="text-[12px] leading-[16px] font-OpenSansRegular text-[#868DA6]">
                          Technicians on this branch see the lunch controls and can record one.
                        </p>
                      </div>
                      <Switch
                        label={`Allow recording lunch at ${rule.branchName}`}
                        checked={rule.allowed !== false}
                        disabled={!editable || saving}
                        onChange={(value) => patchRule(rule, { allowed: value })}
                      />
                    </div>

                    <div className="mt-2 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p
                          className={`text-[13px] leading-[18px] font-OpenSansSemiBold ${
                            rule.allowed === false ? "text-[#A0A6BA]" : "text-[#1A2332]"
                          }`}
                        >
                          Require lunch to be recorded
                        </p>
                        <p className="text-[12px] leading-[16px] font-OpenSansRegular text-[#868DA6]">
                          A worked day with no lunch and no "no lunch taken" is flagged here and
                          on the printed timecard.
                        </p>
                      </div>
                      <Switch
                        label={`Require lunch recording at ${rule.branchName}`}
                        checked={rule.required === true}
                        disabled={!editable || saving || rule.allowed === false}
                        onChange={(value) => patchRule(rule, { required: value })}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-[#E5E8F3] flex items-center justify-between gap-3">
          <p className="text-[12px] leading-[18px] font-OpenSansRegular text-[#868DA6]">
            {editable ? "Changes save as you make them." : "You can view these policies but not change them."}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-4 rounded-lg border border-[#E2E8F0] text-[#1F2A44] text-[14px] font-OpenSansSemiBold"
          >
            Done
          </button>
        </div>
      </div>
    </>
  );
};

export default LunchRulesDrawer;
